"use client";

import { useEffect, useRef, useState } from "react";
import { useSSEWithReconnect } from "@/hooks/use-sse";
import { Wifi, Zap, XCircle, ChevronsDown, ChevronsUp } from "lucide-react";

export default function JobLogs({
    jobId,
    onJobDone,
}: {
    jobId: string | null;
    onJobDone?: () => void;
}) {
    const { logs, status, error } = useSSEWithReconnect(jobId);
    const bottomRef = useRef<HTMLDivElement | null>(null);

    // default hide
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const last = logs[logs.length - 1];
        if (last?.includes('All platforms done')) {
            onJobDone?.();
        }
    }, [logs]);

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [logs.length]);

    const statusColor =
        status === "connected" || status === "open"
            ? "text-emerald-400"
            : status === "connecting"
                ? "text-amber-400"
                : "text-rose-400";

    return (
        <div className="w-full">
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                    <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/10">
                        <Wifi className="w-4 h-4" />
                        <span className="font-mono text-xs text-white/80">SSE</span>
                        <span className={`font-mono text-xs ${statusColor}`}>
                            {status.toUpperCase()}
                        </span>
                    </span>

                    <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/10">
                        <Zap className="w-4 h-4" />
                        <span className="font-mono text-xs text-white/80">
                            JOB ID:
                        </span>
                        <span className="font-mono text-xs text-white/90">
                            {jobId?.slice(0, 8) ?? "—"}
                        </span>
                    </span>
                </div>

                {/* TOGGLE BUTTON */}
                <button
                    onClick={() => setOpen((v) => !v)}
                    className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/10 hover:bg-white/10 transition"
                >
                    {open ? (
                        <>
                            <ChevronsUp className="w-4 h-4" />
                            <span className="font-mono text-xs text-white/80">HIDE</span>
                        </>
                    ) : (
                        <>
                            <ChevronsDown className="w-4 h-4" />
                            <span className="font-mono text-xs text-white/80">SHOW</span>
                        </>
                    )}
                </button>
            </div>

            {/* FULL HIDE */}
            {!open ? null : (
                <div className="relative rounded-2xl border border-white/10 bg-linear-to-b from-black/70 to-white/5 p-4 shadow-lg shadow-black/40">
                    <div className="absolute inset-0 pointer-events-none">
                        <div className="h-1/3 bg-white/5 blur-xl" />
                    </div>

                    <div className="relative h-80 overflow-y-auto rounded-xl border border-white/5 bg-black/60">
                        <div className="p-4 font-mono text-xs text-white/80">
                            {logs.length === 0 ? (
                                <div className="text-white/50">Waiting for logs…</div>
                            ) : (
                                logs.map((msg, idx) => (
                                    <div key={idx} className="mb-2">
                                        <span className="text-emerald-300">»</span>{" "}
                                        <span className="text-white">{msg}</span>
                                    </div>
                                ))
                            )}
                            <div ref={bottomRef} />
                        </div>
                    </div>

                    {error && (
                        <div className="mt-3 rounded-lg border border-rose-500 bg-rose-500/10 p-3">
                            <div className="flex items-center gap-2 text-rose-300 font-mono text-sm">
                                <XCircle className="w-4 h-4" />
                                <span>{error}</span>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
